import { useCallback, useMemo, useState } from 'react';
import { useSaveDraftWorkflowVersionDefinition } from 'src/graphql/entities/workflow/hooks/use-save-draft-workflow-version-definition';
import type { WorkflowVersionRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import {
  definitionContentKey,
  type WorkflowDefinition,
  type WorkflowStepCatalog,
  type WorkflowStepProblem,
} from './utils/workflow-definition';
import {
  wireReferences,
  type WorkflowDefinitionEdit,
  workflowDefinitionReducer,
} from './utils/workflow-definition-edits';
import { collectStepProblems, groupProblemsByNode } from './utils/workflow-step-problems';
import { fieldLabelAt } from './utils/workflow-step-summary';

interface DraftState {
  /** The stored definition this working copy started from; a new one resets it. */
  sourceKey: string;
  /** What the save in flight sent, by content: when the stored copy becomes that, it's ours. */
  savingKey: string | null;
  versionId: string | undefined;
  definition: WorkflowDefinition | null;
  /** Errors from the last save that the user hasn't edited away yet. */
  saveProblems: WorkflowStepProblem[];
  openNodeId: string | null;
}

/**
 * Why a save didn't go through, naming the first problem by its step's name or type: "Not saved:
 * Say — Message: Required.", then how many other steps need attention. A whole-workflow error has no step, so it is the reason.
 */
export const notSavedMessage = (
  errors: WorkflowStepProblem[],
  definition: WorkflowDefinition,
  catalog: WorkflowStepCatalog,
) => {
  const [first] = errors;
  if (!first) return 'Not saved: the definition was refused.';

  const node = first.nodeId
    ? definition.nodes.find(({ id }) => id === first.nodeId)
    : undefined;
  const stepType = node ? catalog.byType.get(node.type) : undefined;
  const field =
    first.field && stepType ? fieldLabelAt(stepType.fields, first.field) : undefined;
  const reason = field ? `${field}: ${first.message}` : first.message;
  const where = node ? `${node.name || stepType?.label || node.type} — ` : '';

  const otherSteps = new Set(
    errors.flatMap(({ nodeId }) => (nodeId && nodeId !== first.nodeId ? [nodeId] : [])),
  ).size;
  const more = otherSteps
    ? ` ${otherSteps === 1 ? '1 more step needs' : `${otherSteps} more steps need`} attention.`
    : '';
  return `Not saved: ${where}${reason}${more}`;
};

const problemKey = ({ nodeId, edgeId, field, message }: WorkflowStepProblem) =>
  [nodeId, edgeId, field, message].join('|');

const parseRaw = (raw: string | null | undefined): Record<string, unknown> => {
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
};

/**
 * The definition to save: the working copy written over the stored JSON, so whatever the editor
 * doesn't model — the definition's `version`, extra keys on a step or a connection — goes back
 * exactly as it came.
 */
const toStoredDefinition = (
  definition: WorkflowDefinition,
  storedRaw: string | null | undefined,
): WorkflowDefinition => {
  const stored = parseRaw(storedRaw);
  const byId = (items: unknown) =>
    new Map(
      (Array.isArray(items) ? items : []).flatMap((item) =>
        item && typeof item === 'object' && typeof (item as { id?: unknown }).id === 'string'
          ? [[(item as { id: string }).id, item as Record<string, unknown>]]
          : [],
      ),
    );
  const storedNodes = byId(stored.nodes);
  const storedEdges = byId(stored.edges);

  // Only the same thing keeps its stored extras: an id freed by a delete and handed to a new step
  // or connection must not bring back the old one's keys, such as an `if` edge's condition.
  // `name` and `width` are modelled, so a stored one must not fill in for one the editor
  // cleared (a name emptied, a width reset)
  const sameNode = (node: WorkflowDefinition['nodes'][number]) => {
    const storedNode = storedNodes.get(node.id);
    if (storedNode?.type !== node.type) return {};
    const { name: _storedName, width: _storedWidth, ...extras } = storedNode;
    return extras;
  };
  const sameEdge = (edge: WorkflowDefinition['edges'][number]) => {
    const storedEdge = edge.id ? storedEdges.get(edge.id) : undefined;
    return storedEdge?.source === edge.source && storedEdge.target === edge.target
      ? storedEdge
      : {};
  };

  return {
    ...stored,
    nodes: definition.nodes.map((node) => ({ ...sameNode(node), ...node })),
    edges: definition.edges.map((edge) => ({ ...sameEdge(edge), ...edge })),
  };
};

/**
 * The working copy of the selected version's definition, and what the header needs to save it.
 *
 * Starts from the version's stored definition with its references wired (see `wireReferences`) —
 * so wiring a reference counts as a change, and is saved like one — and resets when another
 * version is selected, the stored content changes, or Discard is pressed. Problems are the portal's
 * own check merged with the errors from the last save; a save error stays on its step until that
 * step is edited or a step is added or deleted.
 */
export function useWorkflowDraftEditor({
  workflowVersion,
  storedDefinition,
  catalog,
  editable,
  partnerId,
}: {
  workflowVersion: WorkflowVersionRaw | undefined;
  /** The parsed stored definition; `null` when it doesn't parse, and there's nothing to edit. */
  storedDefinition: WorkflowDefinition | null;
  catalog: WorkflowStepCatalog;
  editable: boolean;
  partnerId?: number | null;
}) {
  // Unsaved edits are measured against what is stored. A draft opens with its references wired,
  // which shows as a change until it's saved: what the canvas shows is what will run.
  const startFrom = useMemo(
    () =>
      editable && storedDefinition
        ? wireReferences(storedDefinition, catalog)
        : storedDefinition,
    [catalog, editable, storedDefinition],
  );
  const versionId = workflowVersion?.workflowVersionId;
  const storedKey = useMemo(() => definitionContentKey(storedDefinition), [storedDefinition]);
  // By content, not text: the list and the mutations write the same JSON with keys in different
  // orders. `editable` too: it turns on once the catalog loads, and the copy then restarts wired.
  const sourceKey = `${versionId}:${editable}:${storedKey}`;
  const { saveDraftWorkflowVersionDefinition, isSaving } =
    useSaveDraftWorkflowVersionDefinition({
      partnerId,
    });
  const { showSuccessAlert, showErrorAlert } = useNotifications();

  const [state, setState] = useState<DraftState>(() => ({
    sourceKey,
    savingKey: null,
    versionId,
    definition: startFrom,
    saveProblems: [],
    openNodeId: null,
  }));

  // Another version, or the stored content changed: start over from what is stored. Done while
  // rendering rather than in an effect, so no frame ever shows the old copy against the new
  // version. When the change is our own save landing, the working copy stays — anything typed
  // while the save was on its way is kept, and shows as unsaved. A save keeps the open step;
  // switching versions closes it.
  let current = state;
  if (state.sourceKey !== sourceKey) {
    const ownSave =
      state.versionId === versionId &&
      state.savingKey !== null &&
      state.savingKey === storedKey;
    current = {
      sourceKey,
      savingKey: null,
      versionId,
      definition: ownSave ? state.definition : startFrom,
      saveProblems: [],
      openNodeId: state.versionId === versionId ? state.openNodeId : null,
    };
    setState(current);
  }

  const dispatch = useCallback(
    (edit: WorkflowDefinitionEdit) => {
      setState((previous) => {
        if (!previous.definition) return previous;

        const structural = edit.kind === 'insertStep' || edit.kind === 'deleteStep';
        const next = workflowDefinitionReducer(previous.definition, edit);
        return {
          ...previous,
          // a step added or removed can give a reference a new provider, or take its away
          definition: structural ? wireReferences(next, catalog) : next,
          // An edited step's save errors are stale. Adding or removing a step can fix an error on
          // any other step (an extra connection, a step that's gone), so it clears them all; the
          // canvas's own check still flags what it can see, and the next save says the rest.
          saveProblems: structural
            ? []
            : previous.saveProblems.filter(
                (problem) => !('nodeId' in edit && problem.nodeId === edit.nodeId),
              ),
        };
      });
    },
    [catalog],
  );

  const setOpenNodeId = useCallback(
    (openNodeId: string | null) => setState((previous) => ({ ...previous, openNodeId })),
    [],
  );

  const discard = useCallback(
    () =>
      setState((previous) => ({
        ...previous,
        definition: startFrom,
        saveProblems: [],
        openNodeId: null,
      })),
    [startFrom],
  );

  const { definition, saveProblems, openNodeId } = current;

  const isDirty = useMemo(
    () =>
      definition !== storedDefinition &&
      definitionContentKey(definition) !== definitionContentKey(storedDefinition),
    [definition, storedDefinition],
  );

  // What the canvas can tell by itself; the Save button holds on these. A save error is only
  // shown: the server said it, and saving again is how to hear whether it still holds.
  const clientProblems = useMemo(
    () => (editable && definition ? collectStepProblems(definition, catalog) : []),
    [catalog, definition, editable],
  );

  const problems = useMemo(() => {
    if (!editable || !definition) return [];

    const ids = new Set(definition.nodes.map(({ id }) => id));
    const seen = new Set<string>();
    return [...clientProblems, ...saveProblems].filter((problem) => {
      // an error on a step that's since been removed is gone with it
      if (problem.nodeId && !ids.has(problem.nodeId)) return false;
      const key = problemKey(problem);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [clientProblems, definition, editable, saveProblems]);

  const problemsByNode = useMemo(() => groupProblemsByNode(problems), [problems]);

  const { stepsNeedingAttention, firstProblemNodeId } = useMemo(() => {
    // in stored order, which insertStep keeps close to graph order
    const withProblems = (definition?.nodes ?? []).filter(({ id }) => problemsByNode.has(id));
    return {
      stepsNeedingAttention: withProblems.length,
      firstProblemNodeId: withProblems[0]?.id ?? null,
    };
  }, [definition, problemsByNode]);

  const save = useCallback(async (): Promise<WorkflowVersionRaw | null> => {
    if (!versionId || !definition) return null;

    const sentKey = definitionContentKey(definition);
    setState((previous) => ({ ...previous, savingKey: sentKey }));
    const result = await saveDraftWorkflowVersionDefinition({
      workflowVersionId: versionId,
      definition: toStoredDefinition(definition, workflowVersion?.definition),
    });
    if (!result?.saved) setState((previous) => ({ ...previous, savingKey: null }));
    if (!result) return null;

    if (!result.saved || !result.workflowVersion) {
      const errors = result.errors.map(({ nodeId, edgeId, field, message }) => ({
        nodeId,
        edgeId,
        field,
        message,
      }));
      setState((previous) => ({
        ...previous,
        saveProblems: errors,
        openNodeId: errors.find(({ nodeId }) => nodeId)?.nodeId ?? previous.openNodeId,
      }));
      // A failure that isn't about the definition already alerted in the save hook.
      showErrorAlert(notSavedMessage(errors, definition, catalog));
      return null;
    }

    showSuccessAlert('Draft saved');
    // Once the page shows the saved row, `sourceKey` changes; the copy carries on from there.
    return result.workflowVersion;
  }, [
    catalog,
    definition,
    saveDraftWorkflowVersionDefinition,
    showErrorAlert,
    showSuccessAlert,
    versionId,
    workflowVersion?.definition,
  ]);

  return {
    definition,
    dispatch,
    isDirty,
    problems,
    /** The canvas's own problems, without save errors: what Save holds on. */
    clientProblems,
    problemsByNode,
    /** Distinct steps with at least one problem, for "2 steps need attention". */
    stepsNeedingAttention,
    /** The first step with a problem, in stored order; `null` when none. */
    firstProblemNodeId,
    openNodeId,
    setOpenNodeId,
    discard,
    /** Saves through `saveDraftWorkflowVersionDefinition`; resolves to the saved row, or `null`. */
    save,
    isSaving,
  };
}
